// src/services/rawMaterialInventoryService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class RawMaterialInventoryService {
  
  // 📦 Get all raw material inventories
  async getAllInventories(params = {}) {
    try {
      console.log('📦 Fetching all raw material inventories with params:', params)
      const response = await ApiService.get('/raw-material-inventories', params)
      console.log('✅ Raw material inventories fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching raw material inventories:', error)
      throw error
    }
  }

  // 📦 Get inventory by ID
  async getInventoryById(id) {
    try {
      console.log('📦 Fetching inventory by ID:', id)
      const endpoint = ApiService.replaceUrlParams('/raw-material-inventories/:id', { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Inventory fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching inventory:', error)
      throw error
    }
  }

  // 📦 Create single inventory assignment
  async createInventory(inventoryData) {
    try {
      console.log('📦 Creating new inventory assignment with data:', inventoryData)
      
      // Validate required fields
      this.validateInventoryData(inventoryData)
      
      const response = await ApiService.post('/raw-material-inventories', inventoryData)
      console.log('✅ Inventory created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating inventory:', error)
      throw error
    }
  }

  // 📦 Create multiple inventory assignments (bulk)
  async createMultipleInventories(bulkData) {
    try {
      console.log('📦 Creating multiple inventory assignments:', bulkData)
      
      // Validate bulk data
      this.validateBulkInventoryData(bulkData)
      
      const response = await ApiService.post('/raw-material-inventories/multiple', bulkData)
      console.log('✅ Multiple inventories created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating multiple inventories:', error)
      throw error
    }
  }

  // 📦 Update inventory (stock, price, status)
  async updateInventory(id, inventoryData) {
    try {
      console.log('📦 Updating inventory:', { id, inventoryData })
      
      // Validate partial data for updates
      this.validatePartialInventoryData(inventoryData)
      
      const endpoint = ApiService.replaceUrlParams('/raw-material-inventories/:id', { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      const response = await ApiService.patch(endpoint, inventoryData)
      console.log('✅ Inventory updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating inventory:', error)
      throw error
    }
  }

  // 📦 Delete inventory
  async deleteInventory(id) {
    try {
      console.log('📦 Deleting inventory:', id)
      const endpoint = ApiService.replaceUrlParams('/raw-material-inventories/:id', { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Inventory deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting inventory:', error)
      throw error
    }
  }

  // 📦 Get unassigned raw materials for a branch
  async getUnassignedRawMaterials(branchId) {
    try {
      console.log('📦 Fetching unassigned raw materials for branch:', branchId)
      const endpoint = ApiService.replaceUrlParams('/raw-material-inventories/unassigned-raw-materials/:branchId', { branchId })
      const response = await ApiService.get(endpoint)
      console.log('✅ Unassigned raw materials fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching unassigned raw materials:', error)
      throw error
    }
  }

  // 📦 Get inventories by branch
  async getInventoriesByBranch(branchId, params = {}) {
    try {
      console.log('📦 Fetching inventories for branch:', branchId)
      const queryParams = { branch: branchId, ...params }
      const response = await ApiService.get('/raw-material-inventories', queryParams)
      console.log('✅ Branch inventories fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching branch inventories:', error)
      throw error
    }
  }

  // 📦 Get inventories by raw material
  async getInventoriesByRawMaterial(rawMaterialId, params = {}) {
    try {
      console.log('📦 Fetching inventories for raw material:', rawMaterialId)
      const queryParams = { rawMaterial: rawMaterialId, ...params }
      const response = await ApiService.get('/raw-material-inventories', queryParams)
      console.log('✅ Raw material inventories fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching raw material inventories:', error)
      throw error
    }
  }

  // 📦 Get low stock alerts
  async getLowStockAlerts(branchId = null) {
    try {
      console.log('📦 Fetching low stock alerts for branch:', branchId)
      const params = branchId ? { branch: branchId, lowStock: true } : { lowStock: true }
      const response = await ApiService.get('/raw-material-inventories', params)
      console.log('✅ Low stock alerts fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching low stock alerts:', error)
      throw error
    }
  }

  // 📦 Update stock level
  async updateStock(inventoryId, currentStock) {
    try {
      console.log('📦 Updating stock level:', { inventoryId, currentStock })
      const updateData = { currentStock: Number(currentStock) }
      return await this.updateInventory(inventoryId, updateData)
    } catch (error) {
      console.error('❌ Error updating stock level:', error)
      throw error
    }
  }

  // 📦 Update unit price
  async updateUnitPrice(inventoryId, unitPrice) {
    try {
      console.log('📦 Updating unit price:', { inventoryId, unitPrice })
      const updateData = { unitPrice: Number(unitPrice) }
      return await this.updateInventory(inventoryId, updateData)
    } catch (error) {
      console.error('❌ Error updating unit price:', error)
      throw error
    }
  }

  // 📦 Toggle inventory status
  async toggleInventoryStatus(id, isActive) {
    try {
      console.log('📦 Toggling inventory status:', { id, isActive })
      const updateData = { isActive }
      return await this.updateInventory(id, updateData)
    } catch (error) {
      console.error('❌ Error toggling inventory status:', error)
      throw error
    }
  }

  // 🛡️ Validation Methods
  validateInventoryData(inventoryData) {
    const { branch, rawMaterial } = inventoryData

    // Check required fields
    if (!branch || !branch.trim()) {
      throw new Error('Branch is required')
    }

    if (!rawMaterial || !rawMaterial.trim()) {
      throw new Error('Raw material is required')
    }

    return true
  }

  validateBulkInventoryData(bulkData) {
    const { branch, rawMaterials } = bulkData

    // Check required fields
    if (!branch || !branch.trim()) {
      throw new Error('Branch is required for bulk assignment')
    }

    if (!rawMaterials || !Array.isArray(rawMaterials) || rawMaterials.length === 0) {
      throw new Error('At least one raw material is required for bulk assignment')
    }

    // Validate each raw material
    rawMaterials.forEach((material, index) => {
      if (!material.rawMaterial || !material.rawMaterial.trim()) {
        throw new Error(`Raw material is required at position ${index + 1}`)
      }
    })

    return true
  }

  validatePartialInventoryData(inventoryData) {
    const errors = []

    // Only validate fields that are provided
    if (inventoryData.currentStock !== undefined) {
      const stock = Number(inventoryData.currentStock)
      if (isNaN(stock) || stock < 0) {
        errors.push('Current stock must be a positive number')
      }
    }

    if (inventoryData.unitPrice !== undefined && inventoryData.unitPrice !== null) {
      const price = Number(inventoryData.unitPrice)
      if (isNaN(price) || price < 0) {
        errors.push('Unit price must be a positive number')
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(', '))
    }

    return true
  }

  // 📝 Search and Filter Methods (for client-side)
  searchInventories(inventories, searchTerm) {
    if (!searchTerm) return inventories

    const term = searchTerm.toLowerCase()
    return inventories.filter(inventory => {
      const materialName = inventory.rawMaterial?.name || ''
      const materialCode = inventory.rawMaterial?.code || ''
      const branchName = inventory.branch?.name || ''
      
      return materialName.toLowerCase().includes(term) ||
             materialCode.toLowerCase().includes(term) ||
             branchName.toLowerCase().includes(term)
    })
  }

  filterByStatus(inventories, status) {
    if (!status) return inventories

    return inventories.filter(inventory => {
      const threshold = inventory.rawMaterial?.threshold || 0
      const currentStatus = this.getStockStatus(inventory.currentStock, threshold)
      return currentStatus === status
    })
  }

  filterByBranch(inventories, branchId) {
    if (!branchId) return inventories

    return inventories.filter(inventory => {
      const inventoryBranchId = inventory.branch?._id || inventory.branch
      return inventoryBranchId === branchId
    })
  }

  // Get stock status
  getStockStatus(currentStock, threshold) {
    if (currentStock === 0) {
      return 'out_of_stock'
    } else if (currentStock <= threshold) {
      return 'low_stock'
    } else if (currentStock > threshold * 3) {
      return 'over_stock'
    } else {
      return 'normal'
    }
  }

  // Sort inventories
  sortInventories(inventories, sortBy = 'rawMaterial.name', sortOrder = 'asc') {
    return [...inventories].sort((a, b) => {
      let valueA, valueB

      // Handle nested properties
      if (sortBy.includes('.')) {
        const keys = sortBy.split('.')
        valueA = keys.reduce((obj, key) => obj?.[key], a)
        valueB = keys.reduce((obj, key) => obj?.[key], b)
      } else {
        valueA = a[sortBy]
        valueB = b[sortBy]
      }

      // Handle string comparison
      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB?.toLowerCase() || ''
      }

      // Handle null/undefined values
      if (valueA == null) valueA = ''
      if (valueB == null) valueB = ''

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })
  }

  // 📊 Analytics Methods
  getInventorySummary(inventories) {
    const summary = {
      total: inventories.length,
      active: inventories.filter(inv => inv.isActive).length,
      inactive: inventories.filter(inv => !inv.isActive).length,
      stockStatus: {
        normal: 0,
        lowStock: 0,
        outOfStock: 0,
        overStock: 0
      },
      totalValue: 0
    }

    inventories.forEach(inventory => {
      const threshold = inventory.rawMaterial?.threshold || 0
      const status = this.getStockStatus(inventory.currentStock, threshold)
      
      switch (status) {
        case 'normal':
          summary.stockStatus.normal++
          break
        case 'low_stock':
          summary.stockStatus.lowStock++
          break
        case 'out_of_stock':
          summary.stockStatus.outOfStock++
          break
        case 'over_stock':
          summary.stockStatus.overStock++
          break
      }

      const value = (inventory.currentStock || 0) * (inventory.unitPrice || 0)
      summary.totalValue += value
    })

    return summary
  }

  // 🎨 UI Helper Methods
  getStatusColor(status) {
    const colors = {
      normal: 'bg-blue-100 text-blue-800',
      low_stock: 'bg-yellow-100 text-yellow-800',
      out_of_stock: 'bg-red-100 text-red-800',
      over_stock: 'bg-green-100 text-green-800'
    }
    return colors[status] || colors.normal
  }

  getStatusLabel(status) {
    const labels = {
      normal: 'Normal',
      low_stock: 'Low Stock',
      out_of_stock: 'Out of Stock',
      over_stock: 'Over Stock'
    }
    return labels[status] || 'Unknown'
  }

  // Format display values
  formatStock(currentStock, unit = null) {
    const unitText = unit?.abbreviation || unit?.name || ''
    return `${currentStock} ${unitText}`.trim()
  }

  formatPrice(price) {
    return price ? `Rs. ${Number(price).toLocaleString()}` : 'N/A'
  }

  formatValue(currentStock, unitPrice) {
    const value = (currentStock || 0) * (unitPrice || 0)
    return `Rs. ${value.toLocaleString()}`
  }
}

export default new RawMaterialInventoryService()